import { useEffect, useRef } from 'react';
import { BasicTarget, getTargetElement } from 'web-utils';

export type Target = BasicTarget<HTMLElement | Element | Window | Document>;

type Options = {
  target?: Target;
  capture?: boolean;
  once?: boolean;
  passive?: boolean;
  disabled?: boolean;
};

const useEventListener = (
  eventType: string | string[],
  handler: Function,
  options: Options = {},
) => {
  const handlerRef = useRef<Function>();
  handlerRef.current = handler;

  const events = Array.isArray(eventType) ? eventType : [eventType];

  useEffect(() => {
    const targetElement = getTargetElement(options.target, window)!;
    if (!targetElement.addEventListener) {
      return;
    }

    const eventListener = (
      event: Event,
    ): EventListenerOrEventListenerObject | AddEventListenerOptions => (
      handlerRef.current && handlerRef.current(event)
    );

    const removeEventListener = () => {
      events.forEach(type => {
        targetElement.removeEventListener(type, eventListener, {
          capture: options.capture,
        });
      });
    };

    if (options.disabled) {
      removeEventListener();
      return;
    }

    events.forEach(type => {
      targetElement.addEventListener(type, eventListener, {
        capture: options.capture,
        once: options.once,
        passive: options.passive,
      });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      removeEventListener();
    };
  }, [
    JSON.stringify(eventType),
    options.target,
    options.disabled,
    options.capture,
    options.once,
    options.passive,
  ]);
};

export default useEventListener;
